import moment from "moment";
import get from "lodash.get";

import React, { Fragment } from "react";
import { statusCodes } from "./helperCodes";
import sectionCodes from "./sectionsFixtures";

import history from "../history";

import { isEmpty } from "./utils";

const { COMPLETED } = statusCodes;
const { NOT_STARTED } = statusCodes;
const { IS_REQUIRED } = statusCodes;
const { CONTINUAL } = statusCodes;

export const isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

export const getPageJobId = () => {
  const jobIdEl = document.getElementById("JobId");
  return jobIdEl ? jobIdEl.value : undefined;
};

export const getIsInMembersArea = () =>
  window.location.href.indexOf("my-account") > -1;

export const membersAreaGoto = (href) => {
  if (getIsInMembersArea()) {
    return history.push(href);
  }
  window.location.href = `/my-account${href}`;
  return null;
};

export const getUTCDate = (date) => {
  // return proper UTC date without removing one day from a date
  const offset = date.getTimezoneOffset();
  const utcDate = Date.parse(
    new Date(date.getTime() + Math.abs(offset * 60000))
  );
  return Number.isNaN(utcDate) ? "" : utcDate;
};

export const hhmmComponent = (
  hours,
  minutes,
  hoursOpacity = 1,
  minutesOpacity = 1
) => {
  return (
    <Fragment>
      <div style={{ display: "inline-block" }}>
        <span
          style={{ opacity: hoursOpacity, paddingRight: "0.8rem" }}
          className="font-weight-bold"
        >
          {hours}h
        </span>
        <span style={{ opacity: minutesOpacity }} className="font-weight-bold">
          {minutes}m
        </span>
      </div>
    </Fragment>
  );
};

export const decimalTohhmm = (dec) => {
  let hours;
  let minutes;
  let noHoursOpacity;
  let noMinutesOpacity;
  if (dec === 24) {
    hours = "24";
    minutes = "00";
    noHoursOpacity = "1";
    noMinutesOpacity = "0.5";
  } else {
    const n = new Date(0, 0);
    n.setSeconds(+dec * 60 * 60);
    hours = n.toTimeString().substr(0, 2);
    minutes = n.toTimeString().substr(3, 2);
    const seconds = n.toTimeString().substr(6, 2);
    if (seconds === "59") minutes = String(Number(minutes) + 1);
    if (minutes.length === 1) minutes = `0${minutes}`;
    noHoursOpacity = hours === "00" ? "0.5" : "1";
    noMinutesOpacity = minutes === "00" ? "0.5" : "1";
  }

  return hhmmComponent(hours, minutes, noHoursOpacity, noMinutesOpacity);
};

export const getMonthAndYearFromDate = (date) => {
  const momentDate = moment.utc(date);
  return {
    month: momentDate.month().toString(),
    year: momentDate.format("YYYY"),
  };
};

/**
 * Function will take a date and split it into 3 strings, if the date is invalid the date parts will be blank string
 *
 * @param {string} date
 * @param {[string, string, string]} [keys] - keys to label date parts (day, month, year)
 * @returns {Object} a new object map with the specified keys and date parts.
 */
export const getDateParts = (date, [dayKey, monthKey, yearKey]) => {
  const normalisedInput = Number.isInteger(date) ? date : Date.parse(date);
  const dateObj = new Date(normalisedInput);
  if (moment.isDate(dateObj) && !Number.isNaN(normalisedInput)) {
    return {
      [dayKey]: moment(dateObj).format("D"),
      [monthKey]: moment(dateObj).month().toString(),
      [yearKey]: moment(dateObj).format("YYYY"),
    };
  }
  return { [dayKey]: "", [monthKey]: "", [yearKey]: "" };
};

export const toInt = (str) => Number.parseInt(str, 10);

export const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const monthNames = {
  0: "January",
  1: "February",
  2: "March",
  3: "April",
  4: "May",
  5: "June",
  6: "July",
  7: "August",
  8: "September",
  9: "October",
  10: "November",
  11: "December",
};

export const getValidDate = (year, month, day) => {
  const date = new Date(toInt(year), toInt(month), toInt(day));
  const isValid = date.toString() !== "Invalid Date";
  return isValid ? date : undefined;
};

/**
 * Convert date parts (strings that are in numerical form) into javascript Date object
 *
 * @param {string} day
 * @param {string} month
 * @param {string} year
 * @returns {Date}
 */
export const partsToDate = (day, month, year) =>
  getValidDate(year, month, day === "" ? "1" : day);

/**
 * Returns whether given date is valid
 *
 * @param {Date} date
 */
export const isDateValid = (date) => {
  if (Object.prototype.toString.call(date) === "[object Date]") {
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    return true;
  }
  return false;
};

export const getDurationBetween = (dateFrom, dateTo) => {
  let howManyYears = "";
  let howManyMonths = "";

  // Get `moment` date from years and months
  const momentFrom = moment(dateFrom);
  const momentTo = moment(dateTo);

  // Get `moment` difference in years and months from given dates
  const years = Math.abs(momentFrom.diff(momentTo, "years"));
  const months = Math.abs(momentFrom.diff(momentTo, "months")) % 12;

  /* We need to add 1 to make the difference in months including monthFrom and monthTo. 
  For example we have February 2019 - March 2019 => will make 2 months including both of them */
  const monthsInclusiveFromMonth = months + 1;

  // Render proper string for months and years
  if (years) howManyYears = years > 1 ? `${years} years` : `${years} year`;
  if (monthsInclusiveFromMonth)
    howManyMonths =
      monthsInclusiveFromMonth > 1
        ? `${monthsInclusiveFromMonth} months`
        : `${monthsInclusiveFromMonth} month`;

  return `${howManyYears} ${howManyMonths}`;
};

export const getCurrentMonthAndYear = () => {
  // Month is in form of array and starts from 0 so to get exact value we should use 1 +
  return [1 + moment().month(), moment().year()];
};

export const toReadableDate = (date) => {
  const normalisedInput = Number.isInteger(date) ? date : Date.parse(date);
  const dateObj = new Date(normalisedInput);
  if (moment.isDate(dateObj) && !Number.isNaN(dateObj.getTime())) {
    return {
      toReadableDate: moment(dateObj).format("DD/MM/YYYY"),
      toReadableDay: moment(dateObj).format("D"),
      toReadableMonth: moment(dateObj).format("M"),
      toReadableYear: moment(dateObj).format("YYYY"),
    };
  }
  return "";
};

export const getStatus = (section) => {
  if (section.isHidden) {
    return "hidden";
  }
  if (section.status === CONTINUAL.status) {
    return "visible";
  }
  if (section.isRequired && section.status === COMPLETED.status) {
    return "done";
  }
  if (section.isRequired) {
    return "warning";
  }
  if (section.status === COMPLETED.status) {
    return "done";
  }
  return "error";
};

export const getTextColor = (status) => {
  switch (status) {
    case "done":
      return "darkGrey";
    default:
      return "info";
  }
};

export const getCustomIcon = (name) => {
  switch (name) {
    case "calendar":
      return "/images/icons/calendar.png";
    case "resources":
      return "/images/icons/resources.svg";
    case "onboarding":
      return "/images/icons/onboarding.svg";
    default:
      return "No Custom Icon with provided name found";
  }
};

export const getStatusCTAButton = (status) => {
  switch (status) {
    case "done":
      return ["btn-outline-info", "text-info"];
    default:
      return ["btn-primary", "text-primary"];
  }
};

export const getStatusIcon = (section) => {
  let iconName;
  let iconColor;
  if (section.status === CONTINUAL.status) {
    iconName = CONTINUAL.iconName;
    iconColor = CONTINUAL.iconColor;
  } else if (section.status === COMPLETED.status) {
    if (section.isRequired) {
      iconName = COMPLETED.iconName;
      iconColor = COMPLETED.iconColor;
    }
    iconName = COMPLETED.iconName;
    iconColor = COMPLETED.iconColor;
  } else if (section.isRequired) {
    iconName = IS_REQUIRED.iconName;
    iconColor = IS_REQUIRED.iconColor;
  } else {
    iconName = NOT_STARTED.iconName;
    iconColor = NOT_STARTED.iconColor;
  }
  return [iconName, iconColor];
};

export const renderIcon = (status) => {
  switch (status) {
    case "done":
      return "icomoon-checkmark";
    case "warning":
      return "icomoon-warning";
    case "error":
      return "icomoon-cross-thin";
    case "info":
      return "icomoon-info";
    default:
      return "?";
  }
};

export const findSectionCodes = (sectionId) =>
  sectionCodes.sections.find((section) => section.id === sectionId);

export const getCvUrl = (cvFile) => `/api/candidate/cv/${cvFile.fileKey}`;

export const openCv = (cvFile) => {
  document.location.href = getCvUrl(cvFile);
};

export const getFieldValue = (dataObj, property, defaultValue = "") => {
  if (Array.isArray(property)) {
    return get(dataObj, property) ? get(dataObj, property) : defaultValue;
  }
  return dataObj && dataObj[property] ? dataObj[property] : defaultValue;
};

export const getDate = (date) => {
  const {
    toReadableDay = "",
    toReadableMonth = "",
    toReadableYear = "",
  } = toReadableDate(date);
  return [toReadableDay, toReadableMonth, toReadableYear];
};

export const getAvailability = (date) => {
  const [day, month, year] = getDate(date);

  if (month === "") {
    return [day, month, year];
  }

  // Months are 0 based indices within availablity
  return [day, `${toInt(month) - 1}`, year];
};

export const getWorkHours = (appData) => {
  if (!(appData.fullTimeHours || appData.partTimeHours)) {
    // return default selection
    return [];
  }

  const selected = [];
  if (appData.fullTimeHours) selected.push("full");
  if (appData.partTimeHours) selected.push("part");
  return selected;
};

export const getWorkTypes = (appData) => {
  if (!(appData.permWorkSeeking || appData.tempWorkSeeking)) {
    // return default selection
    return ["temp"];
  }

  const selected = [];
  if (appData.permWorkSeeking) selected.push("permanent");
  if (appData.tempWorkSeeking) selected.push("temp");
  return selected;
};

export const getComponentDisplayName = (Component) =>
  Component.displayName || Component.name || "Component";

export const hasSufficientDataToSendBranchEmail = (appData) => {
  const hasFirstName = !isEmpty(appData.firstName);
  const hasLastName = !isEmpty(appData.lastName);
  const hasFullName = hasFirstName && hasLastName;

  const hasAddress1 = appData.address && !isEmpty(appData.address.addressLine1);
  const hasPostcode = appData.address && !isEmpty(appData.address.postcode);
  const hasAddress = hasAddress1 && hasPostcode;

  const hasPhone =
    !isEmpty(appData.homeTelephone) || !isEmpty(appData.mobileTelephone);

  const hasWorkHistory = appData.workHistory && appData.workHistory.length > 0;
  const hasCV = appData.cvFile || (appData.cv && appData.cv.fileRef);
  const hasCVOrWorkHistory = hasWorkHistory || hasCV;

  const onlineRegistrationTeam = "HAPP";
  const hasBranch =
    !isEmpty(appData.responsibleTeam) &&
    appData.responsibleTeam !== onlineRegistrationTeam;

  const hasGivenConsent = appData.generalConsent === true;

  return (
    hasFullName &&
    hasAddress &&
    hasPhone &&
    hasCVOrWorkHistory &&
    hasBranch &&
    hasGivenConsent
  );
};

export const checkIfDateIsInThisWeek = (date) => {
  const lastSunday = new Date();
  lastSunday.setDate(lastSunday.getDate() - lastSunday.getDay());
  lastSunday.setHours(0, 0, 0, 0);
  const givenDate = new Date(date);
  givenDate.setHours(0, 0, 0, 0);
  return givenDate < lastSunday;
};

export const checkIfBookingIsInFuture = (booking) => {
  const futureBookings = booking.bookings[0].bookedDates.filter(
    (bookedDate) => !checkIfDateIsInThisWeek(bookedDate)
  );
  return futureBookings.length > 0;
};

export const splitBookings = (bookings) => {
  const pastBookings = bookings
    .filter((_booking) => !checkIfBookingIsInFuture(_booking))
    .map((_booking) => {
      return {
        ..._booking,
        isPast: true,
      };
    });
  const currentBookings = bookings
    .filter((_booking) => checkIfBookingIsInFuture(_booking))
    .map((_booking) => {
      return {
        ..._booking,
        isPast: false,
      };
    });
  return { pastBookings, currentBookings };
};

export const nonChangableTeamCodes = ["LIVX"];

export const RTW_INTRODUCTION_DATE = new Date(2000, 0, 1); // 1st January 2000

export const isSouthwestTeam = (responsibleTeamCode) => {
  const southwestCode = ["PERM"];
  return southwestCode.includes(responsibleTeamCode);
};

export const getBranchEmail = (branches, responsibleTeamCode) => {
  if (isSouthwestTeam(responsibleTeamCode)) {
    return "helen.lacey@hrgo.co.uk";
  }

  if (responsibleTeamCode === "HNAT") {
    return "nationalsales@hrgo.co.uk";
  }

  const selectedBranch = branches.find(
    (branch) => branch.code === responsibleTeamCode
  );
  return selectedBranch && !isEmpty(selectedBranch.email)
    ? selectedBranch.email
    : null;
};

export const isCategoryCodeNHS = (code) => {
  const nhsCategoryCodes = ["$035"];
  return nhsCategoryCodes.includes(code);
};

export const isCategoryCodeRoyalMail = (code) => {
  const royalMailCategoryCodes = ["$020", "$021"];
  return royalMailCategoryCodes.includes(code);
};

export const isCategoryCodeDnata = (code) => {
  const dnataCategoryCodes = ["$113"];
  return dnataCategoryCodes.includes(code);
};

export const isCategoryCodeSwissport = (code) => {
  const swissportCategoryCodes = ["$114"];
  return swissportCategoryCodes.includes(code);
};

export const isCategoryCodeGate = (code) => {
  const gateCategoryCodes = ["$115"];
  return gateCategoryCodes.includes(code);
};

export const isCategoryCodeABM = (code) => {
  const abmCategoryCodes = ["$116"];
  return abmCategoryCodes.includes(code);
};

export const getArticle = (word) => {
  const vowels = ["a", "e", "i", "o", "y"];
  return vowels.includes(word[0]) ? "an" : "a";
};

export const isSmallerThanDesktop = (windowWidth) => {
  return windowWidth < 990;
};

export const isSmallerThanTablet = (windowWidth) => {
  return windowWidth < 576;
};

export const excludedUmbracoBranches = ["NONF", "HKDA", "HKCA", "HKCT"];
